// Data seam: sphinx REST (/api/sphinx/servers) + local adapters.
//
// Decisions:
// - servers:* (list / create / createIfMissing / update / delete) → REST.
//   Rows are mapped from `{ id, name, config: ServerFormData }` to the flat
//   `RemoteServer` shape consumers expect; `*WithClientSecret` variants are
//   the plain create/update (secrets live in config.headers/env, there is no
//   separate secret store); `moveServerToProject` is a no-op (one project).
// - `setConnectorEnabled` is the only writer of `config.enabled`, the
//   persisted per-connector on/off flag the Connectors card toggles.
// - projectServerConfig:* → localStorage (`sphinx:projectServerConfig`).
// - serverAttachments:* → localStorage (`sphinx:serverAttachments`); only
//   the Excalidraw quickstart and the suite "server group" picker read them.
// - serverInspections:* → no endpoint; queries resolve to null / [].
// - toolPrechecks:get → no endpoint; resolves to null.
import type { ServerFormData } from "@/shared/types";
import type { RemoteServer } from "@/hooks/useProjects";
import { sphinxApi, type SphinxServer } from "@/lib/sphinx-api";
import {
  invalidateSphinxQueries,
  useSphinxQuery,
  withInvalidate,
} from "@/lib/use-sphinx-query";
import type { DataClient } from "./client";
import { readLocal, useLocalValue, writeLocal } from "./local-store";
import { DEFAULT_PROJECT_ID } from "./project";

// ---- mapping ----------------------------------------------------------------

type ServerConfig = ServerFormData & Record<string, unknown>;

const toMs = (iso: string | null | undefined) => {
  const ms = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(ms) ? ms : 0;
};

export function toRemoteServer(row: SphinxServer): RemoteServer {
  const c = (row.config ?? {}) as ServerConfig;
  const env = c.env ?? undefined;
  const headers = c.headers ?? undefined;
  return {
    _id: row.id,
    projectId: DEFAULT_PROJECT_ID,
    name: c.name || row.name,
    enabled: (c.enabled as boolean | undefined) ?? true,
    transportType: c.type === "stdio" ? "stdio" : "http",
    command: c.command,
    args: c.args,
    env,
    url: c.url,
    headers,
    timeout: c.timeout as number | undefined,
    clientCapabilities: c.clientCapabilities,
    useOAuth: c.useOAuth,
    oauthScopes: c.oauthScopes as string[] | undefined,
    clientId: c.clientId as string | undefined,
    oauthProtocolMode: c.oauthProtocolMode as string | undefined,
    oauthProtocolVersion: c.oauthProtocolVersion as string | undefined,
    oauthRegistrationStrategy: c.oauthRegistrationStrategy as string | undefined,
    hasClientSecret: Boolean(c.clientSecret),
    hasEnv: Boolean(env && Object.keys(env).length > 0),
    hasHeaders: Boolean(headers && Object.keys(headers).length > 0),
    hasBearerToken: Boolean(c.bearerToken),
    oauthResourceUrl: c.oauthResourceUrl as string | undefined,
    useXaa: c.useXaa as boolean | undefined,
    authServerMode: c.authServerMode as RemoteServer["authServerMode"],
    xaaSubject: c.xaaSubject as string | undefined,
    xaaEmail: c.xaaEmail as string | undefined,
    xaaIdentityAssertionFormat: c.xaaIdentityAssertionFormat as string | undefined,
    registrationMode: c.registrationMode as string | undefined,
    xaaClientAuth: c.xaaClientAuth as string | undefined,
    authMethod: c.authMethod as string | undefined,
    createdAt: toMs(row.createdAt),
    updatedAt: toMs(row.updatedAt),
  };
}

/** Flat Convex-style server args → stored `ServerFormData` config. */
function toServerConfig(
  args: Record<string, unknown>,
  base: ServerConfig | undefined
): ServerConfig {
  const {
    projectId: _projectId,
    serverId: _serverId,
    // `config.enabled` is the persisted "connector is switched on" flag and
    // `setConnectorEnabled` is its only writer. Runtime saves carry their own
    // `enabled` mirror (`ServerWithName.enabled`, which the edit path defaults
    // to `false`), so letting it through here would silently switch a
    // connector off on any config save — and the reconciler would then stop
    // connecting it. Drop it and keep whatever the row already stores.
    enabled: _enabled,
    transportType,
    clearClientSecret,
    clearXaaConfig,
    ...rest
  } = args as Record<string, unknown> & {
    transportType?: "stdio" | "http";
    clearClientSecret?: boolean;
    clearXaaConfig?: boolean;
  };
  const next: ServerConfig = { ...(base ?? ({} as ServerConfig)) };
  for (const [k, v] of Object.entries(rest)) {
    if (v === undefined) continue;
    (next as Record<string, unknown>)[k] = v;
  }
  if (transportType) next.type = transportType;
  if (!next.type) next.type = next.command ? "stdio" : "http";
  if (clearClientSecret) delete (next as Record<string, unknown>).clientSecret;
  if (clearXaaConfig) {
    for (const k of Object.keys(next)) {
      if (k.startsWith("xaa")) delete (next as Record<string, unknown>)[k];
    }
  }
  if (!next.name) next.name = String(rest.name ?? "");
  return next;
}

const listServers = () => sphinxApi.servers.list();
const listRemoteServers = async () => (await listServers()).map(toRemoteServer);

// ---- servers:* ------------------------------------------------------------

/** servers:getProjectServers (reactive) */
export function useProjectServersQuery(args: { projectId: string } | "skip") {
  return useSphinxQuery(
    args === "skip" ? null : "servers:list",
    listRemoteServers
  ) as RemoteServer[] | undefined;
}

/** servers:getProjectServers (one-shot) */
export function fetchProjectServers(
  _client: DataClient,
  _args: { projectId: string }
): Promise<RemoteServer[]> {
  return listRemoteServers();
}

/** servers:listForProjects — one project, so every id maps to the same list. */
export function useServersForProjects(args: { projectIds: string[] } | "skip") {
  const servers = useSphinxQuery(
    args === "skip" ? null : "servers:list",
    listRemoteServers
  ) as RemoteServer[] | undefined;
  if (args === "skip" || servers === undefined) return undefined;
  return Object.fromEntries(
    args.projectIds.map((id) => [id, servers])
  ) as Record<string, RemoteServer[]>;
}

async function findServerRow(serverId: string): Promise<SphinxServer | undefined> {
  const rows = await listServers();
  return rows.find((r) => r.id === serverId);
}

const createServer = withInvalidate(
  async (args: Record<string, unknown>): Promise<string> => {
    const config = toServerConfig(args, undefined);
    const created = await sphinxApi.servers.create({
      name: config.name,
      config,
    });
    return created.id;
  }
);

const createServerIfMissing = withInvalidate(
  async (args: Record<string, unknown>): Promise<string> => {
    const name = String(args.name ?? "");
    const rows = await listServers();
    const existing = rows.find(
      (r) => (r.config?.name || r.name) === name
    );
    if (existing) return existing.id;
    const config = toServerConfig(args, undefined);
    const created = await sphinxApi.servers.create({ name: config.name, config });
    return created.id;
  }
);

const updateServer = withInvalidate(
  async (args: Record<string, unknown> & { serverId: string }): Promise<void> => {
    const current = await findServerRow(args.serverId);
    const config = toServerConfig(
      args,
      (current?.config as ServerConfig | undefined) ?? undefined
    );
    await sphinxApi.servers.patch(args.serverId, { name: config.name, config });
  }
);

/**
 * Persist the per-connector enable flag (`config.enabled`), read back by
 * `toRemoteServer` as `RemoteServer.enabled`.
 *
 * Disabling is how a broken connector is taken out of play: the reconciler
 * stops requiring it (`ActiveHostServerReconciler`), so it never reconnects
 * and never re-raises its connect error. This is NOT remove — the row and its
 * config survive, and seeded connectors (which cannot be removed) can still be
 * disabled.
 *
 * `PATCH /servers/:id` replaces `config_json` wholesale (there is no jsonb
 * merge server-side), so the full merged config goes over the wire.
 */
export const setConnectorEnabled = withInvalidate(
  async (args: { serverId: string; enabled: boolean }): Promise<void> => {
    const current = await findServerRow(args.serverId);
    if (!current) {
      throw new Error(`Connector "${args.serverId}" no longer exists`);
    }
    const config: ServerConfig = {
      ...((current.config as ServerConfig | undefined) ?? ({} as ServerConfig)),
      enabled: args.enabled,
    };
    await sphinxApi.servers.patch(args.serverId, { config });
  }
);

const deleteServer = withInvalidate(
  async (args: { serverId: string }): Promise<void> => {
    await sphinxApi.servers.delete(args.serverId);
  }
);

const moveServerToProject = async (_args: {
  serverId: string;
  targetProjectId: string;
}): Promise<void> => {};

const SERVER_MUTATIONS = {
  createServer,
  createServerIfMissing,
  updateServer,
  createServerWithClientSecret: createServer,
  moveServerToProject,
  updateServerWithClientSecret: updateServer,
  deleteServer,
};

// Server mutations for the flat servers table
export function useServerMutations() {
  return SERVER_MUTATIONS;
}

// ---- projectServerConfig:* (localStorage) ----------------------------------

const PROJECT_SERVER_CONFIG_KEY = "projectServerConfig";
const EMPTY_PROJECT_SERVER_CONFIG = null;

/** projectServerConfig:getConfig */
export function useProjectServerConfig(args: { projectId: string } | "skip") {
  return useLocalValue<unknown>(
    args === "skip" ? null : PROJECT_SERVER_CONFIG_KEY,
    EMPTY_PROJECT_SERVER_CONFIG
  );
}

const setProjectServerConfig = async (args: Record<string, unknown>) => {
  const { projectId: _projectId, ...config } = args;
  writeLocal(PROJECT_SERVER_CONFIG_KEY, config);
};

/** projectServerConfig:setConfig */
export function useSetProjectServerConfigMutation() {
  return setProjectServerConfig;
}

// ---- serverInspections:* (no endpoint) -------------------------------------

const NO_REVISIONS: never[] = [];

/** serverInspections:getLatestInspection */
export function useLatestInspection(_args: {
  projectId: string;
  serverId: string;
}) {
  return null;
}

/** serverInspections:listInspectionRevisions */
export function useInspectionRevisions(_args: {
  projectId: string;
  serverId: string;
  limit: number;
}) {
  return NO_REVISIONS;
}

/** serverInspections:getInspectionRevision */
export function useInspectionRevision(_args: {
  projectId: string;
  serverId: string;
  revisionNumber: number;
}) {
  return null;
}

/** serverInspections:diffInspectionRevisions */
export function useInspectionRevisionsDiff(_args: {
  projectId: string;
  serverId: string;
  fromRevisionNumber: number;
  toRevisionNumber: number;
}) {
  return null;
}

// ---- serverAttachments:* (localStorage) ------------------------------------

export type ServerAttachmentRow = {
  _id: string;
  projectId: string;
  name: string;
  serverIds: string[];
  resolvedServerNames: string[];
  scope: "standalone";
  createdAt: number;
  updatedAt: number;
};

const SERVER_ATTACHMENTS_KEY = "serverAttachments";
const NO_ATTACHMENTS: ServerAttachmentRow[] = [];

const readAttachments = () =>
  readLocal<ServerAttachmentRow[]>(SERVER_ATTACHMENTS_KEY, NO_ATTACHMENTS);

const createServerAttachment = async (args: {
  projectId: string;
  name: string;
  serverIds: string[];
}): Promise<{ _id: string }> => {
  const now = Date.now();
  const servers = await listServers();
  const nameById = new Map(servers.map((s) => [s.id, s.config?.name || s.name]));
  const row: ServerAttachmentRow = {
    _id: `attachment_${now.toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    projectId: DEFAULT_PROJECT_ID,
    name: args.name,
    serverIds: [...args.serverIds],
    resolvedServerNames: args.serverIds.map((id) => nameById.get(id) ?? id),
    scope: "standalone",
    createdAt: now,
    updatedAt: now,
  };
  writeLocal(SERVER_ATTACHMENTS_KEY, [...readAttachments(), row]);
  invalidateSphinxQueries();
  return { _id: row._id };
};

const deleteServerAttachment = async (args: {
  serverAttachmentId?: string;
  attachmentId?: string;
}) => {
  const id = args.serverAttachmentId ?? args.attachmentId;
  writeLocal(
    SERVER_ATTACHMENTS_KEY,
    readAttachments().filter((r) => r._id !== id)
  );
  invalidateSphinxQueries();
};

/** serverAttachments:createServerAttachment */
export function useCreateServerAttachmentMutation() {
  return createServerAttachment;
}

/** serverAttachments:deleteServerAttachment */
export function useDeleteServerAttachmentMutation() {
  return deleteServerAttachment;
}

/** serverAttachments:listServerAttachments (one-shot) */
export async function fetchServerAttachments(
  _client: DataClient,
  _args: { projectId: string }
): Promise<ServerAttachmentRow[]> {
  return readAttachments();
}

/** serverAttachments:listServerAttachments (reactive) */
export function useServerAttachments(args: { projectId: string } | "skip") {
  return useLocalValue(
    args === "skip" ? null : SERVER_ATTACHMENTS_KEY,
    NO_ATTACHMENTS
  );
}

// ---- toolPrechecks:* (no endpoint) -----------------------------------------

/** toolPrechecks:get */
export function useToolPrechecks(_args: { snapshot: unknown } | "skip"): unknown {
  return null;
}
